import React, { ReactNode, lazy, Suspense, useState } from 'react'
import { Route, Routes, Outlet } from 'react-router-dom'

import { NoticeBox, Tooltip, VsExclamationIcon } from '@octane/spark'
import LoadingSpinner from 'components/LoadingSpinner'
import UpdateAvailableNotice from 'components/notices/UpdateAvailableNotice'
import {
    useFeatureFlagContext,
    FeatureFlagContextInterface,
} from 'contexts/FeatureFlagContextProvider'
import useDashboard from 'hooks/useDashboard'
import useKeepAppUpToDate from 'hooks/useKeepAppUpToDate'
import { LEADS_PAGE_ROUTE_PATH } from 'pages/LeadsPage'
import AutomobileReadOnly from 'pages/ReadOnly/AutomobileReadOnly'
import ReadOnlyApplication from 'pages/ReadOnly/ReadOnlyApplication'
import AssetType from 'state/enums/AssetType'
import LoadingStatus from 'state/enums/LoadingStatus'
import { formatPhoneNumber } from 'utils/formatPhoneNumber'

import useUser from './hooks/useUser'
import { NestedDealershipRoutes } from './pages/Dealership'
import DecisionResult from './pages/DecisionResult'
import Home from './pages/Home'
import { NestedInvitationRoutes } from './pages/Invitation'
import { NestedMyAccountRoutes } from './pages/MyAccount'
import { NestedResetPasswordRoutes } from './pages/MyAccount/Security/ResetPassword'
import hasPermission from './state/user/userPermissions'
import { User, UserPermissions } from './state/user/UserState'

const ProtectedRoutes = lazy(() => import('components/ProtectedRoutes'))

const CopyIntoNewPage = lazy(() =>
    import('pages/CopyIntoNew').then(({ CopyIntoNew }) => ({
        default: CopyIntoNew,
    })),
)

const DashboardPage = lazy(() => import('pages/Dashboard'))
const DefaultErrorPage = lazy(() => import('pages/DefaultErrorPage'))
const LeadPage = lazy(() => import('pages/LeadsPage'))

const NewUserTourPage = lazy(() =>
    import('pages/NewUserTour').then(({ NewUserTour }) => ({
        default: NewUserTour,
    })),
)

const CreditAccessReportPage = lazy(
    () => import('pages/CreditAccessReportPage'),
)

const SafeCheckReportPage = lazy(() => import('pages/SafeCheckReportPage'))

const SafeCheckReportLegacyRedirect = lazy(
    () => import('pages/SafeCheckReportLegacyRedirect'),
)

const ApplicationStatus = lazy(() => import('./pages/ApplicationStatus'))
const CopyVehicle = lazy(() => import('./pages/CopyVehicle'))
const Dealership = lazy(() => import('./pages/Dealership'))
const Example = lazy(() => import('./pages/Example'))
const FlatCancel = lazy(() => import('./pages/FlatCancel'))
const Invitation = lazy(() => import('./pages/Invitation'))
const MfaEnrollmentPage = lazy(() => import('./pages/MfaEnrollmentPage'))
const MfaVerificationPage = lazy(() => import('./pages/MfaVerificationPage'))
const MyAccount = lazy(() => import('./pages/MyAccount'))
const PerformanceDashboard = lazy(() => import('./pages/PerformanceDashboard'))

const ContractGenerationPage = lazy(
    () => import('./pages/ContractGenerationPage'),
)

const ApplicationStatusListPage = lazy(
    () => import('pages/ApplicationStatusListPage'),
)

const SalesPersonFlexLink = lazy(
    () => import('./pages/Dealership/AssignFlexLinks'),
)

const ResetPasswordPage = lazy(
    () => import('./pages/MyAccount/Security/ResetPassword'),
)

const NewApplication = lazy(() => import('./pages/NewApplication'))
const NotFound = lazy(() => import('./pages/NotFound'))
const ReturnCustomerPage = lazy(() => import('./pages/ReturnCustomerPage'))
const TenDayPayOff = lazy(() => import('./pages/TenDayPayOff'))

const disabledUserNotice = (agentPhoneNumber: string | undefined) => {
    let phoneNumber = '6463705471'

    if (agentPhoneNumber) {
        phoneNumber = agentPhoneNumber
    }

    const formattedPhoneNumber = formatPhoneNumber(phoneNumber)

    return (
        <div>
            You are currently logged into a deactivated account, you will be
            able to continue working on existing deals but do not have the
            ability to submit new applications.
            <Tooltip showOnHover size="small" position="bottom">
                <Tooltip.Trigger>
                    Please contact us if you have any questions.
                </Tooltip.Trigger>
                <Tooltip.Content>
                    <Tooltip.Text>
                        {formattedPhoneNumber || undefined}
                    </Tooltip.Text>
                </Tooltip.Content>
            </Tooltip>
        </div>
    )
}

/**
 * Routes for the new dealer frontend should be added here.
 * They are then reachable at #/<new-route>. Routes for the
 * old dealer frontend live in the octane-dealer-frontend
 * project and are reachable at #!/<old-route> (note the !).
 */

type NestedRoutesType = {
    index: boolean
    element: ReactNode
    path: string
    label?: string
}

export type RouteType = {
    path: string
    element: ReactNode
    nestedRoutes?: NestedRoutesType[]
    permissions?: string[]
}

type AppRoutesParams = {
    readonly routeOverrides?: RouteType[]
}

export const PUBLIC_ROUTES = [
    {
        path: 'password-reset',
        element: <ResetPasswordPage />,
        nestedRoutes: NestedResetPasswordRoutes,
    },
    {
        path: 'invitation/:token/',
        element: <Invitation />,
        nestedRoutes: NestedInvitationRoutes,
    },
    {
        path: 'auth/mfa/verify',
        element: <MfaVerificationPage />,
    },
    {
        path: 'auth/mfa/enroll',
        element: <MfaEnrollmentPage />,
    },
]

const buildRoutes = (
    routes: RouteType[],
    user?: User,
    overrides?: RouteType[],
) => {
    return routes
        .map((route) => {
            const override = overrides?.find((o) => o.path === route.path)

            return override || route
        })
        .filter((route) => {
            if (!route?.permissions) {
                return true
            }

            return route.permissions.every((permission) =>
                hasPermission(user, permission),
            )
        })
        .map((route: RouteType) => (
            <Route path={route.path} element={route.element} key={route.path}>
                {route.nestedRoutes?.map((nestedRoute: NestedRoutesType) => (
                    <Route
                        index={nestedRoute.index}
                        key={nestedRoute.path}
                        element={nestedRoute.element}
                        path={nestedRoute.path}
                    />
                ))}
            </Route>
        ))
}

const getPrivateRoutes = (
    featureFlags: FeatureFlagContextInterface | null,
    canAccessDashboard: boolean,
    user?: User,
): RouteType[] => {
    const primaryAssetType = user?.dealership?.primaryAssetType

    const containsMarineAsSecondaryAssetType =
        user?.dealership?.assetTypes?.includes(AssetType.MARINE)

    const enableMarineAppFormEasyRoute =
        primaryAssetType === AssetType.RECREATIONAL_VEHICLE &&
        containsMarineAsSecondaryAssetType

    return [
        {
            path: 'example',
            element: <Example />,
        },
        {
            path: 'account-settings',
            element: <MyAccount />,
            nestedRoutes: NestedMyAccountRoutes,
        },
        {
            path: 'dealership-settings',
            element: <Dealership />,
            nestedRoutes: NestedDealershipRoutes,
            permissions: [UserPermissions.CAN_VIEW_DEALERSHIP_SETTINGS],
        },
        {
            path: 'new-application',
            element: <NewApplication />,
        },
        // Temporary route for the marine application form for RV dealerships
        // This route will be removed once the new RV application form is implemented
        {
            path: 'new-marine-app',
            element: enableMarineAppFormEasyRoute ? (
                <NewApplication />
            ) : (
                <NotFound />
            ),
        },
        {
            path: 'copy-vehicle/:id/',
            element: <CopyVehicle />,
        },
        {
            path: 'copy-into-new/:id',
            element: <CopyIntoNewPage user={user} />,
        },
        {
            path: 'application/automobile/:id',
            element: <AutomobileReadOnly />,
        },
        {
            path: 'application/:id',
            element: <ReadOnlyApplication />,
        },
        {
            path: 'application/status/:id/',
            element: <ApplicationStatus />,
        },
        {
            path: 'application/decision/:applicationId/',
            element: <DecisionResult />,
        },
        {
            path: 'routeone/application/status/:id/',
            element: <ApplicationStatus />,
        },
        {
            path: LEADS_PAGE_ROUTE_PATH,
            element: featureFlags?.leadsPage ? <LeadPage /> : <NotFound />,
        },
        {
            path: 'credit-access-report',
            element: <CreditAccessReportPage />,
        },
        {
            path: 'new-user-tour',
            element: <NewUserTourPage />,
        },
        {
            path: 'dashboard',
            element: canAccessDashboard ? <DashboardPage /> : <NotFound />,
        },
        {
            path: 'ten-day-payoff',
            element: <TenDayPayOff />,
        },
        {
            path: 'flat-cancel/:applicationId/',
            element: <FlatCancel />,
        },
        {
            path: '/safecheck/report/:uuid',
            element: <SafeCheckReportLegacyRedirect />,
        },
        {
            path: '/safecheck/report/:uuid/:section',
            element: <SafeCheckReportLegacyRedirect />,
        },
        {
            path: '/safecheck/report/:source/:id/:uuid/:status',
            element: <SafeCheckReportPage />,
        },
        {
            path: '/safecheck/report/:source/:id/:uuid/:status/:section',
            element: <SafeCheckReportPage />,
        },
        {
            path: 'saved-applications',
            element: <ApplicationStatusListPage />,
        },
        {
            path: 'performance',
            element: user?.dealership?.isPerformanceMetricEnabled ? (
                <PerformanceDashboard />
            ) : (
                <NotFound />
            ),
        },
        {
            path: '*',
            element: <NotFound />,
        },
        {
            path: 'error',
            element: <DefaultErrorPage navigateHistorySteps={-1} />,
        },
        {
            path: 'return-customer',
            element: (
                <ReturnCustomerPage
                    applicationData={
                        window.windowNavigatorIFrameServer?.applicationData
                    }
                    activeLoans={
                        window.windowNavigatorIFrameServer?.returnCustomerLoans
                    }
                />
            ),
        },
        {
            path: 'dealership-settings/assign-flex-links',
            element: featureFlags?.salesPersonFlexLink ? (
                <SalesPersonFlexLink />
            ) : (
                <NotFound />
            ),
        },
        {
            path: 'contract-generation/:id/',
            element: featureFlags?.modernizedWtdContractingFlow ? (
                <ContractGenerationPage />
            ) : (
                <NotFound />
            ),
        },
    ]
}

const getDashboardAccess = (
    isAutoDealership: boolean,
    isMultiAssetDealership: boolean,
    dashboard: { hasDashboard: boolean; status: LoadingStatus },
) => {
    const { hasDashboard, status: dashboardStatus } = dashboard

    const isDashboardLoading =
        dashboardStatus === LoadingStatus.IDLE ||
        dashboardStatus === LoadingStatus.LOADING ||
        dashboardStatus === LoadingStatus.FAILED

    const hasOrIsLoadingDashboard = hasDashboard || isDashboardLoading

    return (
        (hasOrIsLoadingDashboard || isAutoDealership) && !isMultiAssetDealership
    )
}

function AppRoutes({ routeOverrides }: AppRoutesParams): JSX.Element {
    const [shouldRenderNotifications, setShouldRenderNotifications] =
        useState(false)

    const { user, isLoggedIn, isAutoDealership, isMultiAssetDealership } =
        useUser()

    const featureFlags = useFeatureFlagContext()
    const dashboard = useDashboard()

    const canAccessDashboard = getDashboardAccess(
        isAutoDealership,
        Boolean(isMultiAssetDealership),
        dashboard,
    )

    const isDeactivated = user?.dealership?.isDeactivated

    const privateRoutes = getPrivateRoutes(
        featureFlags,
        canAccessDashboard,
        user,
    )

    const handleShouldRenderNotifications = (value: boolean) => {
        setShouldRenderNotifications(value)
    }

    useKeepAppUpToDate(
        handleShouldRenderNotifications,
        featureFlags?.handleShouldUpdateFlags,
    )

    return (
        <Suspense fallback={<LoadingSpinner />}>
            {shouldRenderNotifications && (
                <UpdateAvailableNotice
                    renderNotification={handleShouldRenderNotifications}
                />
            )}
            {isDeactivated && (
                <NoticeBox
                    title="Attention"
                    icon={VsExclamationIcon}
                    color="warning"
                    variant="box-highlighted"
                >
                    {disabledUserNotice(user?.customerAgent?.agentPhoneNumber)}
                </NoticeBox>
            )}
            <Routes>
                <Route path="/" element={<Outlet />}>
                    {buildRoutes(PUBLIC_ROUTES, user, routeOverrides)}
                    <Route element={<ProtectedRoutes />}>
                        {isLoggedIn && <Route index element={<Home />} />}
                        {buildRoutes(privateRoutes, user, routeOverrides)}
                    </Route>
                </Route>
            </Routes>
        </Suspense>
    )
}

export default AppRoutes
